import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { deleteAccount, logoutBoide } from "@/lib/boide-api";
import { clearBoideUser } from "@/hooks/use-boide-user";
import { signOutFirebase } from "@/lib/firebase";
import { trackEvent, errorName } from "@/lib/analytics";

/** Cụm chữ user phải gõ đúng mới bật được nút xoá — chặn xoá do bấm nhầm. */
const CONFIRM_PHRASE = "XOA TAI KHOAN";

/** Số ngày phải chờ mới đăng ký lại được (khớp ACCOUNT_DELETE_COOLDOWN_DAYS ở BE). */
const COOLDOWN_DAYS_FALLBACK = 90;

const LOSS_ITEMS = [
  "Toàn bộ số dư còn lại trong ví — không được hoàn tiền dưới bất kỳ hình thức nào.",
  "Lịch sử gieo quẻ Kinh Dịch, lá số Tử Vi / Bát Tự và mọi cuộc luận giải với thầy.",
  "Chuỗi điểm danh, hồ sơ se duyên và toàn bộ hoa hồng giới thiệu chưa nhận.",
];

export function DeleteAccountDialog({
  open,
  onOpenChange,
  onDeleted,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Gọi sau khi xoá thành công — để dialog cha (Tài khoản & Hồ sơ) đóng theo. */
  onDeleted?: () => void;
}) {
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Mỗi lần mở lại phải gõ xác nhận từ đầu.
  useEffect(() => {
    if (!open) {
      setConfirmText("");
      setReason("");
    }
  }, [open]);

  // Bỏ dấu trước khi so — user gõ "XÓA TÀI KHOẢN" có dấu vẫn hợp lệ.
  const normalized = confirmText
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/Đ/g, "D")
    .replace(/\s+/g, " ");
  const canDelete = normalized === CONFIRM_PHRASE && !deleting;

  const handleDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    try {
      const result = await deleteAccount(reason);
      const cooldownDays = result.can_recreate_after_days ?? COOLDOWN_DAYS_FALLBACK;
      trackEvent("delete_account", { source: "settings_dialog" });

      // Token đã chết ở BE → dọn sạch phiên local trước khi rời trang.
      logoutBoide();
      clearBoideUser();
      await signOutFirebase();

      onOpenChange(false);
      onDeleted?.();
      toast.success(
        `Đã xoá tài khoản. Bạn có thể đăng ký lại bằng email này sau ${cooldownDays} ngày.`,
        { duration: 8000 },
      );
      navigate({ to: "/" });
    } catch (err) {
      trackEvent("delete_account_failed", { error: errorName(err) });
      toast.error(err instanceof Error ? err.message : "Không xoá được tài khoản");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (deleting ? null : onOpenChange(v))}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-lg">
        <DialogHeader className="px-6 pb-2 pt-6">
          <DialogTitle className="flex items-center gap-2 font-display tracking-[0.15em] text-destructive">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            XOÁ TÀI KHOẢN VĨNH VIỄN
          </DialogTitle>
          <DialogDescription className="font-serif-soft italic">
            Hành động này{" "}
            <strong className="not-italic text-destructive">không thể hoàn tác</strong>. Vui lòng
            đọc kỹ trước khi tiếp tục.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-3">
          <section className="space-y-2 rounded-lg border border-destructive/50 bg-destructive/5 p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-destructive">
              Bạn sẽ mất
            </p>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              {LOSS_ITEMS.map((item) => (
                <li key={item} className="flex gap-2">
                  <span aria-hidden className="mt-[2px] text-destructive">
                    ✕
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <p className="rounded-lg border border-gold-soft/60 bg-background/30 p-3 text-[13px] text-muted-foreground">
            Sau khi xoá, bạn sẽ bị đăng xuất ngay và{" "}
            <strong className="text-foreground">
              chỉ có thể đăng ký lại bằng chính email này sau {COOLDOWN_DAYS_FALLBACK} ngày
            </strong>
            . Nếu chỉ muốn tạm nghỉ, hãy đăng xuất thay vì xoá tài khoản.
          </p>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-medium uppercase tracking-wider text-primary/85">
              Lý do xoá (không bắt buộc)
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              disabled={deleting}
              className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              placeholder="Điều gì khiến bạn rời đi? Góp ý giúp chúng tôi tốt hơn."
            />
          </label>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="delete-confirm" className="text-[11px] uppercase tracking-wider">
              Gõ <span className="font-mono text-destructive">{CONFIRM_PHRASE}</span> để xác nhận
            </Label>
            <Input
              id="delete-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={deleting}
              autoComplete="off"
              placeholder={CONFIRM_PHRASE}
              className="font-mono tracking-widest"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 border-t border-destructive/30 px-6 py-3">
          <Button variant="ghost" disabled={deleting} onClick={() => onOpenChange(false)}>
            Giữ lại tài khoản
          </Button>
          <Button variant="destructive" disabled={!canDelete} onClick={handleDelete}>
            {deleting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="mr-2 h-4 w-4" />
            )}
            {deleting ? "Đang xoá..." : "Xoá vĩnh viễn"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
